type KeyframeBezierHandle = {
  x: number | number[];
  y: number | number[];
};

type KeyframeLike = {
  h?: 0 | 1;
  i?: KeyframeBezierHandle;
  o?: KeyframeBezierHandle;
  s?: number[];
  e?: number[];
  t: number;
};

export type TimelineEasingPreset =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "hold"
  | "custom";

export interface TimelineBezierValue {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const EPSILON = 0.02;

export const TIMELINE_EASING_LABELS: Record<TimelineEasingPreset, string> = {
  linear: "Linear",
  easeIn: "Ease In",
  easeOut: "Ease Out",
  easeInOut: "Ease In Out",
  hold: "Hold",
  custom: "Custom",
};

export const TIMELINE_EASING_PRESETS: Record<
  Exclude<TimelineEasingPreset, "custom" | "hold">,
  TimelineBezierValue
> = {
  linear: { x1: 0.333, y1: 0.333, x2: 0.667, y2: 0.667 },
  easeIn: { x1: 0.42, y1: 0, x2: 1, y2: 1 },
  easeOut: { x1: 0, y1: 0, x2: 0.58, y2: 1 },
  easeInOut: { x1: 0.42, y1: 0, x2: 0.58, y2: 1 },
};

export const DEFAULT_TIMELINE_EASING = TIMELINE_EASING_PRESETS.easeInOut;

function getHandleValue(
  value: number | number[] | undefined,
  fallback: number
): number {
  if (Array.isArray(value)) {
    return typeof value[0] === "number" ? value[0] : fallback;
  }

  return typeof value === "number" ? value : fallback;
}

function fillHandleTemplate(
  template: number | number[] | undefined,
  count: number,
  value: number
): number | number[] {
  if (Array.isArray(template)) {
    return template.map(() => value);
  }

  return count > 1 ? Array.from({ length: count }, () => value) : value;
}

function areClose(left: number, right: number): boolean {
  return Math.abs(left - right) <= EPSILON;
}

function areBezierValuesClose(
  left: TimelineBezierValue,
  right: TimelineBezierValue
): boolean {
  return (
    areClose(left.x1, right.x1) &&
    areClose(left.y1, right.y1) &&
    areClose(left.x2, right.x2) &&
    areClose(left.y2, right.y2)
  );
}

export function getTimelineBezierValue(
  keyframe: KeyframeLike
): TimelineBezierValue {
  if (keyframe.h === 1) {
    return DEFAULT_TIMELINE_EASING;
  }

  return {
    x1: getHandleValue(keyframe.o?.x, DEFAULT_TIMELINE_EASING.x1),
    y1: getHandleValue(keyframe.o?.y, DEFAULT_TIMELINE_EASING.y1),
    x2: getHandleValue(keyframe.i?.x, DEFAULT_TIMELINE_EASING.x2),
    y2: getHandleValue(keyframe.i?.y, DEFAULT_TIMELINE_EASING.y2),
  };
}

export function detectTimelineEasingPreset(
  keyframe: KeyframeLike
): TimelineEasingPreset {
  if (keyframe.h === 1) {
    return "hold";
  }

  const bezier = getTimelineBezierValue(keyframe);
  const matchedPreset = (Object.entries(TIMELINE_EASING_PRESETS) as Array<
    [Exclude<TimelineEasingPreset, "custom" | "hold">, TimelineBezierValue]
  >).find(([, presetValue]) => areBezierValuesClose(bezier, presetValue));

  return matchedPreset?.[0] ?? "custom";
}

export function getTimelineEasingDisplayName(keyframe: KeyframeLike): string {
  const preset = detectTimelineEasingPreset(keyframe);
  return TIMELINE_EASING_LABELS[preset];
}

export function formatTimelineBezierValue(bezier: TimelineBezierValue): string {
  return [bezier.x1, bezier.y1, bezier.x2, bezier.y2]
    .map((value) => value.toFixed(2).replace(/\.?0+$/, ""))
    .join(", ");
}

export function createKeyframeEasingPatch(
  keyframe: KeyframeLike,
  preset: TimelineEasingPreset,
  customBezier?: TimelineBezierValue
): Partial<KeyframeLike> {
  if (preset === "hold") {
    return { h: 1 };
  }

  const bezier =
    preset === "custom"
      ? customBezier ?? DEFAULT_TIMELINE_EASING
      : TIMELINE_EASING_PRESETS[preset];
  const componentCount = Math.max(
    1,
    keyframe.s?.length ?? keyframe.e?.length ?? 1
  );

  return {
    h: 0,
    o: {
      x: fillHandleTemplate(keyframe.o?.x, componentCount, bezier.x1),
      y: fillHandleTemplate(keyframe.o?.y, componentCount, bezier.y1),
    },
    i: {
      x: fillHandleTemplate(keyframe.i?.x, componentCount, bezier.x2),
      y: fillHandleTemplate(keyframe.i?.y, componentCount, bezier.y2),
    },
  };
}

export function createInsertedKeyframe(
  time: number,
  value: number[],
  nextValue?: number[]
): KeyframeLike {
  if (!nextValue) {
    return {
      t: time,
      s: [...value],
    };
  }

  const patch = createKeyframeEasingPatch(
    {
      t: time,
      s: value,
    },
    "easeInOut"
  );

  return {
    t: time,
    s: [...value],
    e: [...nextValue],
    h: patch.h,
    i: patch.i,
    o: patch.o,
  };
}

export function insertOrReplaceKeyframe<
  TKeyframe extends {
    t: number;
  }
>(keyframes: TKeyframe[], nextKeyframe: TKeyframe): TKeyframe[] {
  const withoutExisting = keyframes.filter(
    (keyframe) => keyframe.t !== nextKeyframe.t
  );
  return [...withoutExisting, nextKeyframe].sort(
    (left, right) => left.t - right.t
  );
}
